import React from "react";
import { Flex } from "styles/utility-components";

import DocumentHeader from "components/document_details/DocumentHeader";
import { DocumentViewerFilters } from "components/document_details/FilterForm";
import { PdfViewerContainer } from "components/document_details/PdfViewerContainer";
import {
  createTabItems,
  createTabPanels,
  TAB_KEYS,
  TAB_LABELS,
  TabKey,
  ToolbarContainerLegacy,
  ToolbarContainerNew,
} from "components/document_details/utils";
import { getDocumentViewerSource } from "components/documents/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import EmptyState from "components/ui/empty_state/EmptyState";
import Tabs from "components/ui/tabs/Tabs";
import { FileViewerContextProvider } from "lib/core_components/FileViewer";
import { DOCUMENT_SINGLE_QA, DOCUMENTS_AI_FEATURES, useVariableValue } from "lib/featureFlags";
import { useDocument } from "lib/hooks/api/documents/useDocument";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useSubscription } from "lib/hooks/subscription";
import { TrackingProvider } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";

import css from "./DocumentDetailsPage.module.scss";

const DEFAULT_FILTERS: DocumentViewerFilters = {
  keywords: [],
};

export function DocumentDetailsPage({ documentId }: { documentId: string }) {
  const subscription = useSubscription();
  const hasDocumentsAccess = subscription.hasDataTypes("DOCUMENTS");

  if (!hasDocumentsAccess) {
    return <PaywallBanner featureType="DOCUMENTS" />;
  }

  return <DocumentDetailsPageInner documentId={documentId} />;
}

function DocumentDetailsPageInner({ documentId }: { documentId: string }) {
  const isDocumentsAiEnabled = useVariableValue(DOCUMENTS_AI_FEATURES, false);
  const isDocumentAiSearchEnabled = useVariableValue(DOCUMENT_SINGLE_QA, false);
  const defaultTab: TabKey = isDocumentsAiEnabled ? TAB_KEYS.overview : TAB_KEYS.info;
  const [userSelectedTab, setUserSelectedTab] = React.useState<TabKey | null>(null);

  const { data, isLoading, isError } = useDocument(documentId);
  useLogEntityViewOnOpen(LoggedEntityType.BuyerDocument, documentId);
  const isInFallbackMode =
    getDocumentViewerSource(data?.pdfDocumentUrl, data?.downloadUrl, data?.originalMimeType)
      ?.isInFallbackMode ?? false;

  // Search is disabled when no PDF variant exists: keyword highlighting currently relies on a
  // server-side text-search service that runs against the PDF, so the in-browser
  // rendering fallback path (where pdfDocumentUrl is empty) currently can't support it.
  const isSearchDisabled = !isLoading && !data?.pdfDocumentUrl;

  const candidateTab: TabKey = userSelectedTab ?? defaultTab;
  const activeTab: TabKey =
    candidateTab === TAB_KEYS.search && isSearchDisabled ? defaultTab : candidateTab;

  const tabPanels = React.useMemo(
    () =>
      createTabPanels(
        documentId,
        isLoading,
        DEFAULT_FILTERS,
        data,
        isDocumentsAiEnabled,
        isDocumentAiSearchEnabled,
        undefined,
        isInFallbackMode,
      ),
    [
      documentId,
      isLoading,
      data,
      isDocumentsAiEnabled,
      isDocumentAiSearchEnabled,
      isInFallbackMode,
    ],
  );

  const tabItems = React.useMemo(
    () =>
      createTabItems(
        documentId,
        isLoading,
        isSearchDisabled,
        DEFAULT_FILTERS,
        data,
        isInFallbackMode,
      ),
    [documentId, isLoading, isSearchDisabled, data, isInFallbackMode],
  );

  const tabs = React.useMemo(
    () =>
      TAB_LABELS.map((tab) =>
        tab.key === TAB_KEYS.search ? { ...tab, disabled: isSearchDisabled } : tab,
      ),
    [isSearchDisabled],
  );

  if (isError) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this document"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <div className={css.pageWrapper}>
      <TrackingProvider
        data={{ "Page source": "Document detail page", "Document type": data?.category ?? "-" }}
      >
        <DocumentHeader
          document={{
            id: documentId,
            title: data?.title ?? "",
            downloadUrl: data?.downloadUrl ?? "",
            leads: data?.leads ?? [],
            orgs: data?.buyers ?? [],
            hasBuyerLeads: data?.hasBuyerLeads ?? false,
          }}
          disableTitleLink
          isLoading={isLoading}
          activeTab={isDocumentsAiEnabled ? activeTab : undefined}
          onTabChange={isDocumentsAiEnabled ? setUserSelectedTab : undefined}
          tabs={isDocumentsAiEnabled ? tabs : undefined}
        />
        <div className={css.container}>
          <FileViewerContextProvider>
            <PdfViewerContainer
              pdfDocumentUrl={data?.pdfDocumentUrl ?? ""}
              downloadUrl={data?.downloadUrl ?? ""}
              originalMimeType={data?.originalMimeType}
              isLoading={isLoading}
              hasKeywordsFilter={!!DEFAULT_FILTERS.keywords.length} // no filters applied on initial load
            />
            {isDocumentsAiEnabled ? (
              <ToolbarContainerNew>{tabPanels[activeTab]}</ToolbarContainerNew>
            ) : (
              <ToolbarContainerLegacy>
                <Tabs
                  value={activeTab}
                  items={tabItems}
                  onChange={(key) => setUserSelectedTab(key as TabKey)}
                />
              </ToolbarContainerLegacy>
            )}
          </FileViewerContextProvider>
        </div>
      </TrackingProvider>
    </div>
  );
}
